import type { TranslationKey } from '../i18n'
import {
  CORE_AVG_WEIGHT_WIDGET_ID,
  CORE_MORTALITY_RATE_WIDGET_ID,
  CORE_STOCK_WIDGET_ID,
  FCR_INFLIGHT_BY_LOT_WIDGET_ID,
  LOT_GROWTH_FCR_WIDGET_ID,
  LOT_GROWTH_SFR_WIDGET_ID,
  LOT_GROWTH_SGR_WIDGET_ID,
  seedCoreMonitoringWidgetsIfMissing,
  seedLotCostPerKgWidgetsIfMissing,
  seedLotGrowthPerformanceWidgetsIfMissing,
  upgradeSeededWidgetsToLotUnit,
  type DashboardDefinitionV1
} from './dashboardStorage'
import { seedDefaultHomeWidgetPinsIfNeeded, type HomeWidgetPinV1 } from './homeWidgetsStorage'
import {
  CORE_MONITORING_REPORT_ID,
  LOT_COST_PER_KG_HARVEST_REPORT_ID,
  LOT_COST_PER_KG_INFLIGHT_REPORT_ID,
  LOT_GROWTH_PERFORMANCE_REPORT_ID,
  seedCoreMonitoringReportIfMissing,
  seedDefaultReportsIfEmpty,
  seedLotGrowthPerformanceReportIfMissing,
  type ReportDefinitionV1
} from './reportStorage'

type Translate = (key: TranslationKey) => string

/**
 * v2.2 findings.md #48: the one place that seeds every default saved report
 * and Panel widget. Previously inlined in NewDashboard.tsx's mount effect,
 * so a brand-new user who landed on /home first had no widgets yet for
 * their default Home pins to point at. Every step is idempotent and never
 * overwrites an operator's edit, so calling this from both Panel and Home
 * (in any order, any number of times) is safe. Returns the reports and
 * dashboard as they stand afterwards.
 */
export const seedDefaultPanelContent = (t: Translate): {
  reports: ReportDefinitionV1[]
  dashboard: DashboardDefinitionV1
} => {
  seedDefaultReportsIfEmpty(
    {
      harvest: t('reports.seed.costPerKgHarvest.name'),
      inFlight: t('reports.seed.costPerKgInFlight.name')
    },
    t('reports.seed.costPerKg.name')
  )
  seedLotGrowthPerformanceReportIfMissing(t('reports.seed.lotGrowthPerformance.name'))
  // Each report seed reads the persisted list back fresh, so this last
  // one's return already reflects the two above.
  const reports = seedCoreMonitoringReportIfMissing(t('reports.seed.coreMonitoring.name'))
  const hasReport = (id: string): boolean => reports.some((r) => r.id === id)

  seedLotCostPerKgWidgetsIfMissing({
    harvestReportId: LOT_COST_PER_KG_HARVEST_REPORT_ID,
    harvestReportExists: hasReport(LOT_COST_PER_KG_HARVEST_REPORT_ID),
    harvestWidgetTitle: t('dashboardNew.seed.fcrHarvestByLot.title'),
    inFlightReportId: LOT_COST_PER_KG_INFLIGHT_REPORT_ID,
    inFlightReportExists: hasReport(LOT_COST_PER_KG_INFLIGHT_REPORT_ID),
    inFlightWidgetTitle: t('dashboardNew.seed.fcrInFlightByLot.title')
  })
  seedLotGrowthPerformanceWidgetsIfMissing({
    reportId: LOT_GROWTH_PERFORMANCE_REPORT_ID,
    reportExists: hasReport(LOT_GROWTH_PERFORMANCE_REPORT_ID),
    sgrWidgetTitle: t('dashboardNew.seed.lotGrowthSgr.title'),
    fcrWidgetTitle: t('dashboardNew.seed.lotGrowthFcr.title'),
    sfrWidgetTitle: t('dashboardNew.seed.lotGrowthSfr.title')
  })
  seedCoreMonitoringWidgetsIfMissing({
    reportId: CORE_MONITORING_REPORT_ID,
    reportExists: hasReport(CORE_MONITORING_REPORT_ID),
    mortalityRateWidgetTitle: t('dashboardNew.seed.coreMortalityRate.title'),
    stockWidgetTitle: t('dashboardNew.seed.coreStock.title'),
    avgWeightWidgetTitle: t('dashboardNew.seed.coreAvgWeight.title')
  })
  const dashboard = upgradeSeededWidgetsToLotUnit()

  return { reports, dashboard }
}

// v2.2 findings.md #48 — chosen by the user (2026-09-23): every seeded
// widget except "FCR at harvest", in this order (the GAP/ASC/BAP P0
// streams first, then feed efficiency and growth).
export const DEFAULT_HOME_WIDGET_IDS: readonly string[] = [
  CORE_MORTALITY_RATE_WIDGET_ID,
  CORE_STOCK_WIDGET_ID,
  CORE_AVG_WEIGHT_WIDGET_ID,
  FCR_INFLIGHT_BY_LOT_WIDGET_ID,
  LOT_GROWTH_SGR_WIDGET_ID,
  LOT_GROWTH_FCR_WIDGET_ID,
  LOT_GROWTH_SFR_WIDGET_ID
]

/**
 * Seeds the Panel defaults, then this user's default Home pins (once per
 * user — see `seedDefaultHomeWidgetPinsIfNeeded`). For /home's mount.
 */
export const seedDefaultPanelAndHomeContent = (t: Translate): {
  dashboard: DashboardDefinitionV1
  homePins: HomeWidgetPinV1[]
} => {
  const { dashboard } = seedDefaultPanelContent(t)
  const homePins = seedDefaultHomeWidgetPinsIfNeeded(
    DEFAULT_HOME_WIDGET_IDS,
    new Set(dashboard.widgets.map((widget) => widget.id))
  )
  return { dashboard, homePins }
}
